import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useIsAdmin } from '@/components/auth/useIsAdmin';
import { AdminOnlyEmptyState } from '@/components/auth/admin-only-empty-state';
import { WorkflowEditor } from '@/components/settings/workflow-editor';
import { useDisputeTypeById, useUpdateDisputeTypeWorkflow } from '@/hooks/useDisputeTypesData';
import type { WorkflowData } from '@/components/settings/workflow-editor';

const WorkflowEditorPage: React.FC = () => {
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();
  const { disputeTypeId } = useParams<{ disputeTypeId: string }>();
  const navigate = useNavigate();

  const { data: disputeType, isLoading } = useDisputeTypeById(disputeTypeId ?? null);
  const updateWorkflowMutation = useUpdateDisputeTypeWorkflow(disputeTypeId ?? '');

  const handleSave = (workflow: WorkflowData) => {
    if (updateWorkflowMutation.isPending) return;
    console.log('Saving workflow:', workflow);
    
    // Normalize levels to ensure numeric types for the API payload
    const payload = {
      ...workflow,
      levels: workflow.levels.map(level => ({
        ...level,
        followUpDays: Number(level.followUpDays) || 0,
        escalationDays: Number(level.escalationDays) || 0,
      })),
    };

    // Update the dispute type workflow with the new configuration
    updateWorkflowMutation.mutate(payload, {
      onSuccess: () => {
        console.log('Workflow updated successfully');
      },
      onError: (error) => {
        console.error('Failed to update workflow:', error);
      },
    });
  };

  if (isAdminLoading) {
    return (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <output
          aria-label="Loading"
          className="block h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground"
        />
      </div>
    );
  }
  if (!isAdmin) return <AdminOnlyEmptyState />;

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <p className="text-muted-foreground">Loading dispute type...</p>
        </div>
      </div>
    );
  }

  if (!disputeType) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold">Dispute Type Not Found</h2>
          <p className="text-muted-foreground mt-2">The requested dispute type could not be found.</p>
        </div>
      </div>
    );
  }

  return <WorkflowEditor disputeType={disputeType} onSave={handleSave} isSaving={updateWorkflowMutation.isPending} />;
};

export default WorkflowEditorPage;
